/**
 * ContextMenu — the right-click / long-press menu (DESIGN-SYSTEM.md §5).
 *
 * Two presentations, one API:
 *  - default: a tiny fixed-position popup anchored at the pointer (desktop
 *    right-click). Portaled to <body> so ancestor transform/backdrop-filter
 *    containing blocks don't capture the `fixed` position.
 *  - `sheet`: a phone bottom sheet — dimmed tap-to-close scrim, grabber,
 *    grouped rows, spring entrance.
 *
 * Self-contained: closes on the next click / Escape / resize (+ scroll for
 * the anchored popup; a sheet's own list may scroll), and stops its own
 * clicks from bubbling to that global close. Each item runs its action then
 * closes. Danger items render red and are auto-grouped below a separator
 * (DESIGN-SYSTEM §5 law 2) — the primitive enforces the law so no caller can
 * forget it.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { cx } from "./classNames.js";

export interface ContextMenuItem {
  readonly label: string;
  readonly onClick: () => void;
  /** Destructive action — rendered red (delete etc.). */
  readonly danger?: boolean;
  /** Right scope, wrong state: shown but inert & dimmed (design §5 law 3). */
  readonly disabled?: boolean;
  /** Render a thin rule ABOVE this item to group actions. Optional for
   *  destructive items — the first `danger` item is auto-separated (§5 law 2). */
  readonly separator?: boolean;
}

export interface ContextMenuProps {
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
  readonly onClose: () => void;
  /** Phone presentation: bottom sheet instead of a pointer-anchored popup. */
  readonly sheet?: boolean;
}

export function ContextMenu({ x, y, items, onClose, sheet = false }: ContextMenuProps): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  // Keep the anchored popup inside the viewport: measure after mount and shift
  // left/up if it would overflow (e.g. a menu opened from the right-edge tab
  // strip would otherwise spill off-screen and look like nothing happened).
  const [pos, setPos] = useState({ left: x, top: y });
  useLayoutEffect(() => {
    if (sheet) return;
    const el = ref.current;
    if (!el || typeof el.getBoundingClientRect !== "function") {
      setPos({ left: x, top: y });
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    const m = 8;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const left = width && x + width > vw - m ? Math.max(m, vw - m - width) : x;
    const top = height && y + height > vh - m ? Math.max(m, vh - m - height) : y;
    setPos({ left, top });
  }, [x, y, sheet, items.length]);
  useEffect(() => {
    // Arm the outside-click close on the NEXT tick. A menu opened by a left
    // click mounts during that click's dispatch; React 18 flushes this passive
    // effect synchronously, so without the delay the very click that opened the
    // menu would immediately close it (it bubbles to window after we attach).
    let armed = false;
    const arm = setTimeout(() => {
      armed = true;
    }, 0);
    const close = (): void => {
      if (armed) onClose();
    };
    // A right-click ELSEWHERE dismisses this menu — even on a surface that has
    // no menu of its own (the native menu is suppressed platform-wide). If that
    // surface DOES have a menu, its own opener fires right after and reopens
    // there. Capture phase so we run before the target's opener calls
    // stopPropagation; skip right-clicks inside this menu.
    const onCtx = (e: MouseEvent): void => {
      if (!armed) return;
      const el = ref.current;
      if (el && e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", onCtx, true);
    // The anchored popup dies on any scroll (it would detach from its row);
    // the sheet owns the bottom of the screen and may scroll internally.
    if (!sheet) window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(arm);
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", onCtx, true);
      if (!sheet) window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, sheet]);

  // Portaled to <body>: ancestors with backdrop-filter/transform (the
  // workspace panel blurs) become containing blocks for `fixed`, which
  // pinned the sheet to the PANEL's bottom — above the tab bar — instead
  // of the real viewport bottom.
  if (sheet) {
    return createPortal(
      <div
        ref={ref}
        role="menu"
        className="fixed inset-0 z-overlay"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrim — tap anywhere outside the sheet to dismiss. */}
        <div
          className="arch-scrim-anim absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          onClick={onClose}
        />
        <div className="arch-sheet-anim absolute inset-x-0 bottom-0 max-h-[70%] overflow-y-auto rounded-t-3xl border-t border-white/10 bg-slate-900/(--tint-solid) px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-2.5 shadow-2xl shadow-black/60 backdrop-blur-2xl">
          <div aria-hidden="true" className="mx-auto mb-3 h-1 w-9 rounded-full bg-white/20" />
          <div className="divide-y divide-white/5 overflow-hidden rounded-2xl bg-white/[0.06]">
            {items.map((it, i) => (
              <button
                key={`${it.label}-${i}`}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                aria-disabled={it.disabled}
                onClick={() => {
                  if (it.disabled) return;
                  it.onClick();
                  onClose();
                }}
                className={cx(
                  "block w-full px-4 py-3 text-left text-[15px] transition-colors",
                  it.disabled
                    ? "cursor-default text-slate-500"
                    : it.danger
                      ? "text-red-400 active:bg-white/10"
                      : "text-slate-200 active:bg-white/10",
                )}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-overlay min-w-40 overflow-hidden rounded-md border border-slate-700 bg-slate-900 py-1 shadow-xl shadow-black/50"
    >
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`}>
          {/* §5 law 2: destructive actions sit below a separator. Enforced at
              the root — a rule renders above an explicit `separator` OR above
              the FIRST item of a danger group, so a caller that forgets
              `separator: true` on a delete still gets the grouping. Idempotent:
              one rule even when the danger item also sets `separator`. */}
          {i > 0 && (it.separator || (it.danger && !items[i - 1]?.danger)) ? (
            <div role="separator" className="my-1 border-t border-slate-700" />
          ) : null}
          <button
            type="button"
            role="menuitem"
            disabled={it.disabled}
            aria-disabled={it.disabled}
            onClick={() => {
              if (it.disabled) return;
              it.onClick();
              onClose();
            }}
            className={cx(
              "block w-full px-3 py-1.5 text-left text-xs transition-colors",
              it.disabled
                ? "cursor-default text-slate-600"
                : it.danger
                  ? "text-red-400 hover:bg-slate-800 hover:text-red-300"
                  : "text-slate-300 hover:bg-slate-800 hover:text-slate-100",
            )}
          >
            {it.label}
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}
